<script setup>
import { computed, watch, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { default as HeaderEl } from '@/components/shared/Header.vue';
import { default as FooterEl } from '@/components/footer/Footer.vue';
import DimmerOverlay from '@/components/dimmer/DimmerOverlay.vue';
import i18n from '@/utils/i18n';
import IdleWatcher from '@/utils/idleWatcher.vue';
import auth from '@/utils/auth';
import { useInstitutionStore } from '@/stores/institution';
import { useDepartmentStore } from '@/stores/department';
import { useCacheStore } from '@/stores/cache';
import { useProfilesStore } from '@/stores/profiles';
import { useGeneralStore } from '@/stores/general';
import { useGalleriesStore } from '@/stores/galleries';
import { useIdleHandlerStore } from '@/stores/idleHandler';

const departmentStore = useDepartmentStore();
const institutionStore = useInstitutionStore();
const profilesStore = useProfilesStore();
const cache = useCacheStore();
const generalStore = useGeneralStore();
const galleriesStore = useGalleriesStore();
const idleHandlerStore = useIdleHandlerStore();
const route = useRoute();
const router = useRouter();

const isIdle = computed(() => idleHandlerStore.isIdle);

const onStartPage = computed(() => {
  return route.path === '/';
});

watch(isIdle, async (newValue) => {
  const mainLoginKey = sessionStorage.getItem('main_login_key');
  const tempLoginKey = sessionStorage.getItem('temp_login_key');

  // If theres a difference we are currently not on the main department so switch back.
  if (tempLoginKey && mainLoginKey !== tempLoginKey) {
    await departmentStore.switchDepartment();
    return;
  }

  galleriesStore.setSelectedCategory('');

  if (route.path !== '/') {
    router.push('/');
    return;
  }

  if (newValue === true) {
    // force the widgets to re-render
    generalStore.setIdle();
  }
});

onMounted(async () => {
  await auth.authenticate();

  try {
    await cache.use(
      'institutionStore.getInstitution',
      institutionStore.getInstitution
    );

    generalStore.setIdle();
    generalStore.setCurrentDate(new Date());

    await cache.use(
      'departmentStore.getDepartment',
      departmentStore.getDepartment
    );

    institutionStore.getExternalConnections();

    cache.use('profilesStore.getProfiles', profilesStore.getProfiles);

    i18n.global.locale.value = institutionStore.locale;
  } catch (err) {
    throw new Error(
      'Could not get institution / department data, retrying',
      err
    );
  }

  const DIMENSION_INSTITUTION = 1;
  const DIMENSION_DEPARTMENT = 2;

  window._paq.push([
    'setCustomDimension',
    DIMENSION_INSTITUTION,
    institutionStore.id,
  ]);
  window._paq.push([
    'setCustomDimension',
    DIMENSION_DEPARTMENT,
    departmentStore.id,
  ]);

  generalStore.setLoading(false);
});
</script>

<template>
  <div
    id="app"
    :class="{
      isLoading: generalStore.isLoading,
    }"
    class="flex flex-col relative bg-neutral-100"
  >
    <HeaderEl />
    <div
      v-if="!generalStore.isLoading"
      class="flex-1 relative overflow-y-scroll overflow-x-hidden route-wrapper"
    >
      <router-view :key="$route.path" />
    </div>

    <FooterEl />

    <DimmerOverlay v-if="onStartPage" />
    <IdleWatcher v-if="departmentStore.settings.screenConfig.timeout > 0" />
  </div>
</template>

<style lang="scss">
.tick-credits {
  display: none !important;
  opacity: 0 !important;
}

#app {
  width: 1080px;
  height: 1920px;
  margin: auto;

  .content-views {
    transition: opacity 500ms;
  }

  .route-wrapper {
    .contentArea {
      animation-duration: 1.3s;
    }

    &.scroll-up .contentArea {
      margin-top: 1200px;
    }
  }
}
</style>
